import { Node, Position } from 'reactflow';
import { householdConnections } from './EdgeConfigurations';
import { nodeOrbitLayout } from '../utils/nodeOrbitLayout';
import { ICareMap, IHouseholdMember, IPatient, ISupportGroupKey, ISupportGroupMember } from '../caremap/ICareMap';
import { HoneycombBuilder } from '../honeycomb/HoneycombBuilder';

export type INodeType = 'patient' | 'household' | 'familyNode' | 'honeycombGroup';

// P = parent item, C = child items
export type INodeData<P, C = void> = P & {
    label?: string;
    items?: C[];
    hexagonPositions?: [number, number][];
    indicator?: {
        supportGroupKey?: string;
        identifier: boolean;
        handleStyle: {
            top: number;
            left: number;
        };
        useForOnboarding: boolean;
    }
    selectedItemId?: string;
};

export type IMergedNode<P, C = void> = Node<INodeData<P, C>, INodeType>;

export const householdNodeSizes: {[key: string]: number} = {
    'small': 140,
    'large': 280
};

// TODO: hardcoded to allow only 7 family members and the angles for helping positioning around the patient
export const maxHouseholdMembers = 7;

interface ISupportGroupPositionConfig {
    indicatorCenter: [number, number],
    hexagonOffsets: [number, number][],
    patientHandle: {
        position: Position;
        style?: { top?: number; left?: number;}
    }
}

const supportGroupPositions: ISupportGroupPositionConfig[] = [
    // index 0 = bottom left, going clockwise
    {
        indicatorCenter: [-375, 720],
        hexagonOffsets: [ [-1, 1], [-1, 0], [0, -1], [-2, 0], [-2, -1], [-1, -1], [-2, 1], [-3, 1], [-3, 0], [-3, -1], [-4, 0], [-4, -1] ],
        patientHandle: {
            position: Position.Left,
            style: { top: 150 }
        }
    },
    {
        indicatorCenter: [-315, 335],
        hexagonOffsets: [ [0, 1], [-1, 1], [-1, 0], [-1, 2], [-2, 1], [-2, 0], [-2, 2], [-3, 2], [-3, 1], [-3, 0], [-4, 1], [-4, 0] ],
        patientHandle: {
            position: Position.Left,
            style: { top: 50, left: 30 }
        }
    },
    {
        indicatorCenter: [0, 300],
        hexagonOffsets: [ [-1, 1], [0, 1], [1, 1], [-1, 2], [0, 2], [1, 2], [-1, 3], [0, 3], [1, 3], [-2, 2], [-2, 3], [-3, 3] ],
        patientHandle: {
            position: Position.Top,
            style: { top: 20, left: 150 }
        }
    },
    {
        indicatorCenter: [320, 400],
        hexagonOffsets: [ [1, 1], [0, 1], [1, 0], [1, 2], [2, 1], [2, 0], [3, 1], [3, 0], [3, 2], [2, 2], [1, 3], [0, 2], [3, 3], [4, 2], [4, 1], [4, 0], [2, 3], [4, 3], [3, 4], [1, 4], [0, 3], [5, 1], [5, 2], [5, 3] ],
        patientHandle: {
            position: Position.Right,
            style: { left: 100 }
        }
    },
    {
        indicatorCenter: [375, 720],
        hexagonOffsets: [ [1, 1], [1, 0], [0, -1], [2, 0], [2, -1], [1, -1], [3, 0], [3, -1], [3, 1], [4, 0], [4, -1], [4, 1] ],
        patientHandle: {
            position: Position.Right
        }
    }
];

export const getSupportGroupPositionByKey = (key: ISupportGroupKey) => {
    const order: ISupportGroupKey[] = ['community', 'school', 'medical', 'specialists', 'financial'];
    return supportGroupPositions[order.indexOf(key)];
};

export const getMaxHoneycombHexagonsByKey = (key: ISupportGroupKey) => {
    const position = getSupportGroupPositionByKey(key);
    return position.hexagonOffsets.length;
};

// pick best group to use in onboarding tour (the first group in this array that has members)
const getSupportGroupKeyForOnboarding = (careMap: Partial<ICareMap>) => {
    const orderedKeys: ISupportGroupKey[] = ['medical', 'specialists', 'community', 'school', 'financial'];
    for (const key of orderedKeys) {
        if (careMap[key]?.members?.length) {
            return key;
        }
    }
    return orderedKeys[0];
};

// this is intended only for the ReactFlow caremap, don't try to extract data out of here, USE STATE
export class NodeAdapter {

    static inject = () => [HoneycombBuilder];
    constructor(private builder: HoneycombBuilder) {}

    private shouldShowGroup = (careMap: Partial<ICareMap>, groupKey: ISupportGroupKey) => {
        return Boolean(careMap[groupKey]?.isRelevant);
    };

    adapt = (careMap: Partial<ICareMap>, options: { selectedItemId?: string }  = {}) => {
        const onboardingKey = getSupportGroupKeyForOnboarding(careMap);

        const nodes: IMergedNode<any, any>[] = [];

        if (careMap.patient) {
            const patient: IMergedNode<IPatient, void> = {
                id: 'patient',
                type: 'patient',
                parentNode: 'household-group',
                position: { x: 0, y: 0 },
                width: householdNodeSizes['large'],
                data: {
                    ...careMap.patient,
                    label: careMap.patient.firstName,
                    selectedItemId: options.selectedItemId
                }
            };

            nodes.push(patient);

            if (careMap.household) {
                const household: IMergedNode<IHouseholdMember>[] = careMap.household.members.map((member, i) => ({
                    id: `household-${i}`,
                    type: 'familyNode',
                    width: householdNodeSizes['small'],
                    parentNode: 'household-group',
                    position: { x: 0, y: 0 }, // this gets hacked later by nodeOrbitLayout()
                    data: {
                        label: member.firstName,
                        ...member,
                        selectedItemId: options.selectedItemId
                    }
                }));

                nodeOrbitLayout({
                    radius: 200, // TODO: might want to calculate this based on patient node size
                    maxMoons: maxHouseholdMembers,
                    startAngles: [270, 295, 320, 345, 372, 398, 425],
                    planet: patient,
                    moons: household
                });

                nodes.push({
                    id: 'household-group',
                    type: 'household',
                    position: {
                        x: -150,
                        y: 500
                    },
                    data: {},
                });

                nodes.push(...household);
            }
        }

        if (this.shouldShowGroup(careMap, 'community')) {
            const { indicatorCenter, hexagonOffsets } = getSupportGroupPositionByKey('community');
            const { position, style, handleStyle, hexagonPositions } = this.builder.buildNodeConfig({
                indicatorCenter,
                hexagonOffsets,
                memberCount: careMap.community?.members.length || 0
            });

            nodes.push({
                id: 'community-group',
                type: 'honeycombGroup',
                position,
                style,
                data: {
                    hexagonPositions,
                    indicator: {
                        handleStyle,
                        supportGroupKey: 'community',
                        identifier: true,
                        useForOnboarding: (onboardingKey === 'community')
                    },
                    items: careMap.community!.members, // ! because got passed shouldShowGroup, community exists
                    selectedItemId: options.selectedItemId
                },
            } as IMergedNode<void, ISupportGroupMember>);
        }

        if (this.shouldShowGroup(careMap, 'financial')) {
            const { indicatorCenter, hexagonOffsets } = getSupportGroupPositionByKey('financial');
            const { position, style, handleStyle, hexagonPositions } = this.builder.buildNodeConfig({
                indicatorCenter,
                hexagonOffsets,
                memberCount: careMap.financial?.members.length || 0
            });

            nodes.push({
                id: 'financial-group',
                type: 'honeycombGroup',
                position,
                style,
                data: {
                    hexagonPositions,
                    indicator: {
                        handleStyle,
                        supportGroupKey: 'financial',
                        identifier: true,
                        useForOnboarding: (onboardingKey === 'financial')
                    },
                    items: careMap.financial!.members,
                    selectedItemId: options.selectedItemId
                }
            } as IMergedNode<void, ISupportGroupMember>);
        }

        if (this.shouldShowGroup(careMap, 'school')) {
            const { indicatorCenter, hexagonOffsets } = getSupportGroupPositionByKey('school');
            const { position, style, handleStyle, hexagonPositions } = this.builder.buildNodeConfig({
                indicatorCenter,
                hexagonOffsets,
                memberCount: careMap.school?.members.length || 0
            });

            nodes.push({
                id: 'school-group',
                type: 'honeycombGroup',
                position,
                style,
                data: {
                    hexagonPositions,
                    indicator: {
                        handleStyle,
                        supportGroupKey: 'school',
                        identifier: true,
                        useForOnboarding: (onboardingKey === 'school')
                    },
                    items: careMap.school!.members,
                    selectedItemId: options.selectedItemId
                }
            } as IMergedNode<void, ISupportGroupMember>);
        }

        if (this.shouldShowGroup(careMap, 'medical')) {
            const { indicatorCenter, hexagonOffsets } = getSupportGroupPositionByKey('medical');
            const { position, style, handleStyle, hexagonPositions } = this.builder.buildNodeConfig({
                indicatorCenter,
                hexagonOffsets,
                memberCount: careMap.medical?.members.length || 0
            });

            nodes.push({
                id: 'medical-group',
                type: 'honeycombGroup',
                position,
                style,
                data: {
                    hexagonPositions,
                    indicator: {
                        handleStyle,
                        supportGroupKey: 'medical',
                        identifier: true,
                        useForOnboarding: (onboardingKey === 'medical')
                    },
                    items: careMap.medical!.members,
                    selectedItemId: options.selectedItemId
                }
            } as IMergedNode<void, ISupportGroupMember>);
        }

        if (this.shouldShowGroup(careMap, 'specialists')) {
            const { indicatorCenter, hexagonOffsets } = getSupportGroupPositionByKey('specialists');
            const { position, style, handleStyle, hexagonPositions } = this.builder.buildNodeConfig({
                indicatorCenter,
                hexagonOffsets,
                memberCount: careMap.specialists?.members.length || 0
            });

            nodes.push({
                id: 'specialists-group',
                type: 'honeycombGroup',
                position,
                style,
                data: {
                    hexagonPositions,
                    indicator: {
                        handleStyle,
                        supportGroupKey: 'specialists',
                        identifier: true,
                        useForOnboarding: (onboardingKey === 'specialists')
                    },
                    items: careMap.specialists!.members,
                    selectedItemId: options.selectedItemId
                }
            } as IMergedNode<void, ISupportGroupMember>);
        }

        return { nodes, edges: householdConnections };
    };
}
